(() => {
  if (window.__ohCrispHeaderAssetLoaded) return;
  window.__ohCrispHeaderAssetLoaded = true;

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const installAjaxProductForms = () => {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest && event.target.closest('.ohc-product-form, .ohc-chip__form');
      if (!form) return;
      if (form.dataset.ohcSubmitting === 'true') {
        event.preventDefault();
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.renderContents !== 'function' || typeof cartDrawer.getSectionsToRender !== 'function') {
        return;
      }

      const submitButton = event.submitter || form.querySelector('[type="submit"]');
      if (!submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;

      event.preventDefault();
      form.dataset.ohcSubmitting = 'true';
      const submitButtonWasDisabled = submitButton.disabled;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');

      let status = form.querySelector('[data-ohc-cart-status]');
      if (!status) {
        status = document.createElement('span');
        status.className = 'visually-hidden';
        status.dataset.ohcCartStatus = '';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        form.append(status);
      }
      status.textContent = '';

      let visibleError = form.querySelector('[data-ohc-cart-error]');
      if (!visibleError) {
        visibleError = document.createElement('p');
        visibleError.className = 'ohc-cart-error';
        visibleError.dataset.ohcCartError = '';
        visibleError.hidden = true;
        form.append(visibleError);
      }
      visibleError.hidden = true;
      visibleError.textContent = '';

      const formData = new FormData(form);
      formData.append(
        'sections',
        cartDrawer
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',')
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cartDrawer.setActiveElement === 'function') {
        const drawerRecommendation = form.closest('cart-drawer');
        const fallbackTrigger = document.querySelector('#cart-icon-bubble');
        cartDrawer.setActiveElement(drawerRecommendation && fallbackTrigger ? fallbackTrigger : submitButton);
      }

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        let state = null;
        try {
          state = await response.json();
        } catch (_error) {
          // A non-JSON response is handled by the friendly fallback below.
        }

        if (!response.ok || !state || state.status) {
          const customerMessage =
            typeof state?.description === 'string' && state.description.length < 240
              ? state.description
              : 'This item could not be added. Please refresh and try again.';
          throw new Error(customerMessage);
        }

        // Dawn keeps an empty-state class on the drawer root until the first
        // successful add. Clear it before rendering so the new line item is
        // visible and the drawer uses its normal, interactive layout.
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(state);
        status.textContent = 'Added to cart.';

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'ohcrisp-product-form',
            productVariantId: formData.get('id'),
            cartData: state,
          });
        }
      } catch (error) {
        const customerMessage = error.message || 'This item could not be added. Please refresh and try again.';
        status.textContent = customerMessage;
        visibleError.textContent = customerMessage;
        visibleError.hidden = false;
        console.error('[OhCrisp cart]', error);
      } finally {
        delete form.dataset.ohcSubmitting;
        submitButton.disabled = submitButtonWasDisabled;
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
      }
    });
  };

  class OhCrispAnnouncement extends HTMLElement {
    connectedCallback() {
      this.destroy();

      this.controller = new AbortController();
      this.items = Array.from(this.querySelectorAll('[data-ohc-announcement-item]'));
      this.activeIndex = Math.max(
        0,
        this.items.findIndex((item) => item.classList.contains('is-active'))
      );
      this.interval = Math.max(3000, Number(this.dataset.interval) || 7000);
      this.autoplay = this.dataset.autoplay === 'true';
      this.hoverPaused = false;
      this.focusPaused = false;
      this.editorPaused = false;
      this.timer = null;

      this.items.forEach((item, index) => this.setItemState(item, index === this.activeIndex, true));

      const signal = this.controller.signal;
      this.addEventListener(
        'mouseenter',
        () => {
          this.hoverPaused = true;
          this.schedule();
        },
        { signal }
      );
      this.addEventListener(
        'mouseleave',
        () => {
          this.hoverPaused = false;
          this.schedule();
        },
        { signal }
      );
      this.addEventListener(
        'focusin',
        () => {
          this.focusPaused = true;
          this.schedule();
        },
        { signal }
      );
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) {
            this.focusPaused = false;
            this.schedule();
          }
        },
        { signal }
      );
      document.addEventListener('visibilitychange', () => this.handleVisibility(), { signal });
      reducedMotionQuery.addEventListener('change', () => this.schedule(), { signal });

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), { signal });
        document.addEventListener('shopify:block:deselect', (event) => this.handleBlockDeselect(event), {
          signal,
        });
      }

      this.schedule();
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      window.clearTimeout(this.timer);
      this.timer = null;
      if (this.controller) this.controller.abort();
    }

    setItemState(item, active, immediate = false) {
      if (immediate) item.style.transition = 'none';
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
      item.inert = !active;
      if (immediate) {
        requestAnimationFrame(() => {
          item.style.removeProperty('transition');
        });
      }
    }

    show(index, immediate = false) {
      if (!this.items.length) return;
      const nextIndex = (index + this.items.length) % this.items.length;
      if (nextIndex === this.activeIndex && !immediate) return;

      this.items.forEach((item, itemIndex) => this.setItemState(item, itemIndex === nextIndex, immediate));
      this.activeIndex = nextIndex;
    }

    schedule() {
      window.clearTimeout(this.timer);
      this.timer = null;

      if (
        !this.autoplay ||
        this.items.length < 2 ||
        this.hoverPaused ||
        this.focusPaused ||
        this.editorPaused ||
        document.hidden ||
        reducedMotionQuery.matches
      ) {
        return;
      }

      this.timer = window.setTimeout(() => {
        this.show(this.activeIndex + 1);
        this.schedule();
      }, this.interval);
    }

    handleVisibility() {
      this.schedule();
    }

    handleBlockSelect(event) {
      const item = event.target.closest && event.target.closest('[data-ohc-announcement-item]');
      if (!item || !this.contains(item)) return;
      const selectedIndex = this.items.indexOf(item);
      if (selectedIndex < 0) return;

      this.editorPaused = true;
      this.show(selectedIndex, true);
      this.schedule();
    }

    handleBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.editorPaused = false;
      this.schedule();
    }
  }

  class OhCrispFloatingHeader extends HTMLElement {
    connectedCallback() {
      this.destroy();

      this.controller = new AbortController();
      this.chrome = this.querySelector('[data-ohc-header-chrome]');
      this.menuButton = this.querySelector('[data-ohc-menu-button]');
      this.menu = this.querySelector('[data-ohc-menu]');
      this.overlay = this.querySelector('[data-ohc-menu-overlay]');
      this.cartTrigger = this.querySelector('#cart-icon-bubble');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.headerSection = this.closest('.section-header');
      this.mainContent = document.querySelector('#MainContent');
      this.hideOffset = Math.max(72, Number(this.dataset.hideOffset) || 96);
      this.directionThreshold = Math.max(6, Number(this.dataset.directionThreshold) || 8);
      this.lastScrollY = Math.max(0, window.scrollY);
      this.pendingScrollY = this.lastScrollY;
      this.direction = null;
      this.directionTravel = 0;
      this.raf = 0;
      this.previousFocus = null;
      this.menuOpen = false;
      this.cartOpen = false;
      this.focusWithin = false;
      this.editorMenuOpen = false;
      this.closeTimer = 0;
      this.firstSectionRaf = 0;
      this.firstSectionSurface = null;
      this.scrollLockStyles = null;

      const signal = this.controller.signal;
      this.menuButton?.addEventListener('click', () => this.toggleMenu(), { signal });
      this.querySelector('[data-ohc-menu-close]')?.addEventListener('click', () => this.closeMenu(), { signal });
      this.overlay?.addEventListener('click', () => this.closeMenu(), { signal });
      this.menu?.addEventListener('keydown', (event) => this.handleMenuTabKey(event), { signal });
      document.addEventListener('keydown', (event) => this.handleDocumentKeydown(event), {
        capture: true,
        signal,
      });
      this.addEventListener(
        'focusin',
        () => {
          this.focusWithin = true;
          this.classList.remove('is-hidden');
        },
        { signal }
      );
      this.addEventListener(
        'focusout',
        (event) => {
          if (this.contains(event.relatedTarget)) return;
          this.focusWithin = false;
          this.requestScrollUpdate();
        },
        { signal }
      );
      window.addEventListener('scroll', () => this.requestScrollUpdate(), { passive: true, signal });
      const handleViewportResize = () => {
        const currentScrollY = Math.max(0, window.scrollY);
        this.classList.remove('is-hidden');
        this.lastScrollY = currentScrollY;
        this.pendingScrollY = currentScrollY;
        this.direction = null;
        this.directionTravel = 0;
        this.requestScrollUpdate();
        this.requestFirstSectionSync();
      };
      window.addEventListener('resize', handleViewportResize, { passive: true, signal });
      window.visualViewport?.addEventListener('resize', handleViewportResize, { passive: true, signal });
      window.addEventListener('load', () => this.requestFirstSectionSync(), { signal });

      reducedMotionQuery.addEventListener('change', () => this.requestScrollUpdate(), { signal });

      if (document.body.classList.contains('ohc-page--interior') && this.mainContent) {
        this.firstSectionObserver = new MutationObserver(() => this.requestFirstSectionSync());
        this.firstSectionObserver.observe(this.mainContent, { childList: true });

        document.addEventListener('shopify:section:load', () => this.requestFirstSectionSync(), { signal });
        document.addEventListener('shopify:section:unload', () => this.requestFirstSectionSync(), { signal });
        document.addEventListener('shopify:section:reorder', () => this.requestFirstSectionSync(), { signal });
        this.requestFirstSectionSync();
      }

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => this.handleEditorBlockSelect(event), {
          signal,
        });
        document.addEventListener('shopify:block:deselect', (event) => this.handleEditorBlockDeselect(event), {
          signal,
        });
      }

      if (this.cartDrawer) {
        this.cartObserver = new MutationObserver(() => this.syncCartState());
        this.cartObserver.observe(this.cartDrawer, { attributes: true, attributeFilter: ['class'] });
        this.syncCartState();
      }

      this.prepareCartFallback();
      this.requestScrollUpdate();
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
      if (this.firstSectionRaf) cancelAnimationFrame(this.firstSectionRaf);
      this.firstSectionRaf = 0;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = 0;
      if (this.controller) this.controller.abort();
      if (this.cartObserver) this.cartObserver.disconnect();
      this.cartObserver = null;
      if (this.firstSectionObserver) this.firstSectionObserver.disconnect();
      this.firstSectionObserver = null;
      this.clearFirstSectionSurface();
      document.body.classList.remove('ohc-first-section-ready');
      if (this.menuOpen || this.scrollLockStyles) this.releaseMenu(false, true);
    }

    requestFirstSectionSync() {
      if (this.firstSectionRaf || !this.isConnected) return;
      this.firstSectionRaf = requestAnimationFrame(() => {
        this.firstSectionRaf = 0;
        this.syncFirstSectionLayout();
      });
    }

    syncFirstSectionLayout() {
      if (
        !this.headerSection ||
        !this.mainContent ||
        !document.body.classList.contains('ohc-page--interior')
      ) {
        this.clearFirstSectionSurface();
        document.body.classList.remove('ohc-first-section-ready');
        return;
      }

      const firstSection = Array.from(this.mainContent.children).find((section) => {
        if (!section.classList.contains('shopify-section') || section.hidden) return false;
        const style = window.getComputedStyle(section);
        const rect = section.getBoundingClientRect();
        return style.display !== 'none' && style.visibility !== 'hidden' && rect.height > 0;
      });

      if (!firstSection) {
        this.clearFirstSectionSurface();
        document.body.classList.remove('ohc-first-section-ready');
        return;
      }

      this.clearFirstSectionSurface();

      const firstRect = firstSection.getBoundingClientRect();
      const ignoredTags = new Set(['LINK', 'SCRIPT', 'STYLE', 'TEMPLATE']);
      const isAlignedSurface = (element) => {
        if (ignoredTags.has(element.tagName) || element.hidden) return false;

        const style = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.position !== 'absolute' &&
          style.position !== 'fixed' &&
          rect.height > 0 &&
          rect.width >= firstRect.width * 0.78 &&
          rect.top <= firstRect.top + 3 &&
          rect.bottom > firstRect.top
        );
      };

      const directSurface = Array.from(firstSection.children).find(isAlignedSurface);
      let surface = null;

      /*
       * Prefer the section's rendered root when it owns the real color,
       * gradient, image, or video. This keeps custom Article/Product/Page
       * surfaces continuous behind the transparent header.
       */
      if (directSurface && this.isPaintedSurface(directSurface)) {
        surface = directSurface;
      } else if (this.isPaintedSurface(firstSection)) {
        surface = firstSection;
      } else if (directSurface) {
        const queue = Array.from(directSurface.children).map((element) => ({
          element,
          depth: 1,
        }));
        let inspected = 0;

        while (queue.length && inspected < 80) {
          const { element, depth } = queue.shift();
          inspected += 1;

          if (isAlignedSurface(element) && this.isPaintedSurface(element)) {
            surface = element;
            break;
          }

          if (depth >= 4) continue;
          Array.from(element.children).forEach((child) => {
            if (ignoredTags.has(child.tagName)) return;
            queue.push({ element: child, depth: depth + 1 });
          });
        }
      }

      surface = surface || directSurface || firstSection;
      const basePaddingTop = Number.parseFloat(
        window.getComputedStyle(surface).paddingTop
      );

      surface.style.setProperty(
        '--ohc-first-section-base-padding-top',
        `${Number.isFinite(basePaddingTop) ? basePaddingTop : 0}px`
      );
      surface.classList.add('ohc-first-section-surface');
      this.firstSectionSurface = surface;
      document.body.classList.add('ohc-first-section-ready');
    }

    clearFirstSectionSurface() {
      if (!this.firstSectionSurface) return;

      this.firstSectionSurface.classList.remove('ohc-first-section-surface');
      this.firstSectionSurface.style.removeProperty('--ohc-first-section-base-padding-top');
      this.firstSectionSurface = null;
    }

    isVisibleBackgroundColor(color) {
      if (!color || color === 'transparent') return false;
      const rgba = color.match(
        /^rgba\(\s*[^,]+,\s*[^,]+,\s*[^,]+,\s*([0-9]*\.?[0-9]+)\s*\)$/i
      );
      return !rgba || Number(rgba[1]) > 0.01;
    }

    isPaintedSurface(element) {
      if (!element) return false;
      const style = window.getComputedStyle(element);
      return (
        this.isVisibleBackgroundColor(style.backgroundColor) ||
        (style.backgroundImage && style.backgroundImage !== 'none')
      );
    }

    prepareCartFallback() {
      if (!this.cartTrigger) return;

      window.setTimeout(() => {
        if (!this.isConnected || !this.cartTrigger || this.cartTrigger.getAttribute('role') === 'button') return;
        if (!this.cartDrawer || typeof this.cartDrawer.open !== 'function') return;

        this.cartTrigger.setAttribute('role', 'button');
        this.cartTrigger.setAttribute('aria-haspopup', 'dialog');
        this.cartTrigger.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.closeMenu(false);
            this.cartDrawer.open(this.cartTrigger);
          },
          { signal: this.controller.signal }
        );
        this.cartTrigger.addEventListener(
          'keydown',
          (event) => {
            if (event.code !== 'Space') return;
            event.preventDefault();
            this.closeMenu(false);
            this.cartDrawer.open(this.cartTrigger);
          },
          { signal: this.controller.signal }
        );
      }, 0);
    }

    requestScrollUpdate() {
      this.pendingScrollY = Math.max(0, window.scrollY);
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.updateScrollState(this.pendingScrollY);
      });
    }

    updateScrollState(scrollY) {
      if (scrollY <= 2) {
        this.classList.remove('is-scrolled', 'is-hidden');
        this.lastScrollY = scrollY;
        this.direction = null;
        this.directionTravel = 0;
        return;
      }

      this.classList.add('is-scrolled');

      const delta = scrollY - this.lastScrollY;
      if (delta !== 0) {
        const nextDirection = delta > 0 ? 'down' : 'up';
        if (nextDirection !== this.direction) {
          this.direction = nextDirection;
          this.directionTravel = 0;
        }
        this.directionTravel += Math.abs(delta);
      }

      if (this.menuOpen || this.cartOpen || this.focusWithin || scrollY <= this.hideOffset) {
        this.classList.remove('is-hidden');
      } else if (this.direction === 'down' && this.directionTravel >= this.directionThreshold) {
        this.classList.add('is-hidden');
        this.directionTravel = 0;
      } else if (this.direction === 'up' && this.directionTravel >= 6) {
        this.classList.remove('is-hidden');
        this.directionTravel = 0;
      }

      this.lastScrollY = scrollY;
    }

    toggleMenu() {
      if (this.menuOpen) {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    }

    openMenu(options = {}) {
      if (!this.menu || !this.menuButton || this.menuOpen) return;

      if (this.cartDrawer && this.cartDrawer.classList.contains('active') && typeof this.cartDrawer.close === 'function') {
        this.cartDrawer.close();
      }

      window.clearTimeout(this.closeTimer);
      this.closeTimer = 0;
      this.previousFocus = options.preserveFocus ? null : document.activeElement;
      this.menuOpen = true;
      this.classList.remove('is-hidden');
      this.menuButton.setAttribute('aria-expanded', 'true');
      this.menuButton.setAttribute('aria-label', 'Close menu');
      this.menu.hidden = false;
      this.menu.inert = false;
      this.menu.setAttribute('aria-hidden', 'false');
      if (this.overlay) {
        this.overlay.hidden = false;
        this.overlay.inert = false;
      }
      this.lockPage();

      requestAnimationFrame(() => {
        if (!this.menuOpen || !this.isConnected) return;
        this.classList.add('is-menu-open');
        if (!options.preserveFocus) {
          const focusTarget = this.getFocusableElements()[0] || this.menu;
          focusTarget.focus({ preventScroll: true });
        }
      });
    }

    closeMenu(restoreFocus = true) {
      if (!this.menuOpen) return;
      this.releaseMenu(restoreFocus);
    }

    releaseMenu(restoreFocus, immediate = false) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = 0;
      this.menuOpen = false;
      this.classList.remove('is-menu-open');
      this.menuButton?.setAttribute('aria-expanded', 'false');
      this.menuButton?.setAttribute('aria-label', 'Open menu');
      if (this.menu) this.menu.setAttribute('aria-hidden', 'true');
      if (this.menu) this.menu.inert = true;
      if (this.overlay) this.overlay.inert = true;
      this.unlockPage();

      if (
        restoreFocus &&
        this.previousFocus &&
        this.previousFocus !== document.body &&
        this.previousFocus !== document.documentElement &&
        document.contains(this.previousFocus) &&
        typeof this.previousFocus.focus === 'function'
      ) {
        this.previousFocus.focus({ preventScroll: true });
      } else if (restoreFocus) {
        this.menuButton?.focus({ preventScroll: true });
      }
      this.previousFocus = null;

      const finishClose = () => {
        this.closeTimer = 0;
        if (this.menuOpen) return;
        if (this.menu) this.menu.hidden = true;
        if (this.overlay) this.overlay.hidden = true;
      };

      if (immediate || reducedMotionQuery.matches || !this.isConnected) {
        finishClose();
      } else {
        this.closeTimer = window.setTimeout(finishClose, 190);
      }

      if (this.isConnected) this.requestScrollUpdate();
    }

    lockPage() {
      if (this.scrollLockStyles) return;
      const bodyStyle = document.body.style;
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const currentPadding = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;

      this.scrollLockStyles = {
        overflow: bodyStyle.overflow,
        paddingRight: bodyStyle.paddingRight,
      };
      bodyStyle.overflow = 'hidden';
      if (scrollbarWidth > 0) bodyStyle.paddingRight = `${currentPadding + scrollbarWidth}px`;
      document.body.classList.add('ohc-header-menu-open');
    }

    unlockPage() {
      if (!this.scrollLockStyles) {
        document.body.classList.remove('ohc-header-menu-open');
        return;
      }

      document.body.style.overflow = this.scrollLockStyles.overflow;
      document.body.style.paddingRight = this.scrollLockStyles.paddingRight;
      document.body.classList.remove('ohc-header-menu-open');
      this.scrollLockStyles = null;
    }

    getFocusableElements() {
      if (!this.menu) return [];
      return Array.from(
        this.menu.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && !element.closest('[hidden]'));
    }

    handleMenuTabKey(event) {
      if (event.key !== 'Tab') return;
      const focusable = this.getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.menu.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleDocumentKeydown(event) {
      if (!this.menuOpen || event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      this.closeMenu();
    }

    handleEditorBlockSelect(event) {
      if (!event.target || !this.contains(event.target)) return;
      const editableBlock = event.target.closest('[data-ohc-menu-block]');
      if (!editableBlock) return;

      this.editorMenuOpen = true;
      this.openMenu({ preserveFocus: true });
      requestAnimationFrame(() => {
        if (this.menuOpen && this.contains(editableBlock)) {
          editableBlock.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    handleEditorBlockDeselect(event) {
      if (!this.editorMenuOpen || !event.target || !this.contains(event.target)) return;
      this.editorMenuOpen = false;
      this.closeMenu(false);
    }

    syncCartState() {
      const nextCartOpen = Boolean(this.cartDrawer && this.cartDrawer.classList.contains('active'));
      if (nextCartOpen === this.cartOpen) return;

      this.cartOpen = nextCartOpen;
      this.classList.toggle('is-cart-open', nextCartOpen);
      if (nextCartOpen) {
        this.closeMenu(false);
        this.classList.remove('is-hidden');
      } else {
        this.requestScrollUpdate();
      }
    }
  }

  if (!customElements.get('ohcrisp-announcement')) {
    customElements.define('ohcrisp-announcement', OhCrispAnnouncement);
  }

  if (!customElements.get('ohcrisp-floating-header')) {
    customElements.define('ohcrisp-floating-header', OhCrispFloatingHeader);
  }

  installAjaxProductForms();
})();
